'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const navItems = [
  {
    label: 'Buy',
    href: '/property-listings-page',
    megaMenu: [
      { label: 'Apartments', href: '/property-listings-page' },
      { label: 'Villas', href: '/property-listings-page' },
      { label: 'Penthouses', href: '/property-listings-page' },
      { label: 'Townhouses', href: '/property-listings-page' },
      { label: 'Luxury Properties', href: '/property-listings-page' },
      { label: 'Commercial', href: '/property-listings-page' },
    ],
  },
  {
    label: 'Rent',
    href: '/property-listings-page',
    megaMenu: [
      { label: 'Apartments', href: '/property-listings-page' },
      { label: 'Villas', href: '/property-listings-page' },
      { label: 'Holiday Homes', href: '/property-listings-page' },
      { label: 'Serviced Apartments', href: '/property-listings-page' },
      { label: 'Commercial', href: '/property-listings-page' },
    ],
  },
  {
    label: 'Off-Plan',
    href: '/property-listings-page',
    megaMenu: [
      { label: 'New Launches', href: '/property-listings-page' },
      { label: 'Under Construction', href: '/property-listings-page' },
      { label: 'Ready Soon', href: '/property-listings-page' },
      { label: 'Payment Plans', href: '/property-listings-page' },
    ],
  },
  {
    label: 'Invest',
    href: '/property-listings-page',
    megaMenu: [
      { label: 'Investment Opportunities', href: '/property-listings-page' },
      { label: 'ROI Calculator', href: '/property-listings-page' },
      { label: 'Market Insights', href: '/property-listings-page' },
    ],
  },
  { label: 'Advanced Search', href: '/advanced-search-page', megaMenu: [] },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [activeMega, setActiveMega] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-card/95 backdrop-blur-xl shadow-card border-b border-border'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-18">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2.5 flex-shrink-0">
              <AppLogo src="/assets/images/OWW2-1780875471137.png" size={40} />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems?.map((item) => (
                <div
                  key={`nav-${item?.label}`}
                  className="relative"
                  onMouseEnter={() => item?.megaMenu?.length > 0 && setActiveMega(item?.label)}
                  onMouseLeave={() => setActiveMega(null)}
                >
                  <Link
                    href={item?.href}
                    className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                      scrolled
                        ? 'text-foreground/80 hover:text-primary hover:bg-primary/8'
                        : 'text-white/90 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {item?.label}
                    {item?.megaMenu?.length > 0 && (
                      <Icon
                        name="ChevronDownIcon"
                        size={14}
                        className={`transition-transform duration-200 ${activeMega === item?.label ? 'rotate-180' : ''}`}
                      />
                    )}
                  </Link>

                  {/* Mega Menu Dropdown */}
                  {item?.megaMenu?.length > 0 && activeMega === item?.label && (
                    <div className="absolute top-full left-0 mt-1 w-52 bg-card rounded-2xl shadow-elevated border border-border py-2 animate-scale-in">
                      {item?.megaMenu?.map((sub) => (
                        <Link
                          key={`mega-${item?.label}-${sub?.label}`}
                          href={sub?.href}
                          className="block px-4 py-2.5 text-sm text-foreground/80 hover:text-primary hover:bg-primary/8 transition-colors duration-150 font-medium"
                        >
                          {sub?.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-3">
              {/* List Property */}
              <Link
                href="/property-listings-page"
                className={`hidden md:flex items-center gap-1.5 text-sm font-semibold transition-colors duration-200 ${
                  scrolled ? 'text-primary' : 'text-white/90 hover:text-white'
                }`}
              >
                <Icon name="PlusCircleIcon" size={16} />
                List Property
              </Link>

              {/* Login */}
              <Link
                href="/user-dashboard"
                className={`hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border transition-all duration-200 ${
                  scrolled
                    ? 'border-primary text-primary hover:bg-primary hover:text-white' :'border-white/50 text-white hover:bg-white/15'
                }`}
              >
                <Icon name="UserIcon" size={15} />
                Sign In
              </Link>

              {/* Admin */}
              <Link
                href="/admin-dashboard"
                className={`hidden xl:inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  scrolled
                    ? 'bg-primary text-white hover:bg-primary-dark' :'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                }`}
              >
                <Icon name="Squares2X2Icon" size={15} />
                Admin
              </Link>

              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileOpen(true)}
                className={`lg:hidden p-2 rounded-lg transition-colors ${
                  scrolled ? 'text-foreground hover:bg-muted' : 'text-white hover:bg-white/15'
                }`}
                aria-label="Open menu"
              >
                <Icon name="Bars3Icon" size={22} />
              </button>
            </div>
          </div>
        </div>
      </header>
      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[100] lg:hidden">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-card shadow-modal flex flex-col animate-slide-up">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div className="flex items-center gap-2">
                <AppLogo src="/assets/images/OWW2-1780875471137.png" size={36} />
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="XMarkIcon" size={20} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto p-4 space-y-1">
              {navItems?.map((item) => (
                <Link
                  key={`mobile-nav-${item?.label}`}
                  href={item?.href}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-foreground hover:text-primary hover:bg-primary/8 transition-all duration-200"
                >
                  {item?.label}
                </Link>
              ))}
            </nav>

            <div className="p-4 border-t border-border space-y-3">
              <Link
                href="/user-dashboard"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border-2 border-primary text-primary font-semibold text-sm hover:bg-primary hover:text-white transition-all duration-200"
              >
                <Icon name="UserIcon" size={16} />
                Sign In
              </Link>
              <Link
                href="/admin-dashboard"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-dark transition-all duration-200"
              >
                <Icon name="Squares2X2Icon" size={16} />
                Admin Panel
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}